import Vue from "vue";
import om from "../api";
import get from "lodash/get";
import router from "@/router";
import { tiposAReferir } from "../config/constants";
import { cleanHtmlTags } from "../utils";
import * as ACTIONS from "../config/constants/action-types";
import * as END_POINTS from "../config/constants/end-points";
import * as MUTATIONS from "../config/constants/mutation-types";

export default {
  /**
   * Action disparada por el router en
   * cada actualización
   * @param {Object} context
   * @param {Object} payload
   */
  [ACTIONS.DO_ROUTE](context, payload) {
    context.commit(MUTATIONS.ON_ROUTE, payload);
  },
  /**
   * Action para guardar el tracking
   * code de la campaña
   * @param {Object} context
   * @param {Object} payload
   */
  [ACTIONS.DO_TCODE](context, payload) {
    context.commit(MUTATIONS.ON_TCODE, payload);
  },
  /**
   * Mostrar u ocultar el loader
   * @param {Object} context
   * @param {Object} payload
   */
  [ACTIONS.DO_IS_LOADING](context, payload) {
    context.commit(MUTATIONS.ON_IS_LOADING, payload);
  },
  /**
   * Action para mostrar notificaciones en el front
   * en modo "toast"
   * @param {Object} context
   * @param {Object} payload: {type: String ["info", "success", "alert"], message: String}
   */
  [ACTIONS.DO_NOTIFICATION_TOAST](context, payload) {
    context.commit(MUTATIONS.ON_NOTIFICATION_TOAST, payload);
    setTimeout(() => {
      // Luego de 3 segundos quitamos la notificacion
      context.dispatch(ACTIONS.DO_NOTIFICATION_TOAST_RESET);
    }, 3000);
  },
  [ACTIONS.DO_NOTIFICATION_TOAST_RESET](context) {
    context.commit(MUTATIONS.ON_NOTIFICATION_TOAST, {
      type: "",
      message: ""
    });
  },
  /**
   * Action genérico para llamadas a la API
   * Se debe pasar el endpoint, el action a ejecutar en caso
   * de éxito y el action en caso de error
   * @param {Object} context Vue Context
   * @param {endPoint, payload, doSuccess, doError} actionPayload
   */
  [ACTIONS.DO_REQUEST_API](context, { endPoint, payload, doSuccess, doError }) {
    om.request({ endPoint, payload })
      .then(response => {
        if (response.data.errorCode) {
          context.dispatch(doError, response.data.errorCode);
        } else {
          context.dispatch(doSuccess, {
            ...response.data
          });
        }
      })
      .catch(error => {
        context.dispatch(doError, error);
      });
  },

  /**
   * Actions disparadas por el
   * step navigator para avanzar pasos
   * Cada action guarda el paso
   * actual en el store
   * @param {Object} context
   */
  [ACTIONS.DO_LANDING]: (context, payload) => {
    // Indicar cargando
    context.commit(MUTATIONS.ON_IS_LOADING, true);

    // Llamamos al action genérico de request
    // y especificamos el action de éxito y el de error
    // junto con el endpoint + payload
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.CONSULTAR_OFERTA,
      doSuccess: ACTIONS.DO_LANDING_SUCCESS,
      doError: ACTIONS.DO_LANDING_ERROR,
      payload
    });
  },
  [ACTIONS.DO_LANDING_SUCCESS]: (context, payload) => {
    // Request landing exitosa
    context.commit(MUTATIONS.ON_LANDING, payload);
    // Ir a paso 1
    router.push("paso1");
  },
  [ACTIONS.DO_LANDING_ERROR]: (context, payload) => {
    // Consulta landing con error
    Vue.$log.error(payload);
    // Mostrar mensaje de error
    context.dispatch(ACTIONS.DO_MENSAJE, "mensaje-4");
  },

  [ACTIONS.DO_PASO1]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.commit(MUTATIONS.ON_PASO_1, payload);
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.CONSULTAR_CAMPANIA,
      doSuccess: ACTIONS.DO_PASO1_SUCCESS,
      doError: ACTIONS.DO_PASO1_ERROR,
      payload
    });
  },
  [ACTIONS.DO_PASO1_SUCCESS]: async (context, payload) => {
    context.dispatch(ACTIONS.DO_TOKENIZE_USER, {
      nroCliente: get(payload, "respuesta.numeroCliente", "")
    });

    // Cargar contenido dinámico de campañas desde AEM
    const campaigns = payload.respuesta?.campanias || [];
    if (campaigns.length > 0) {
      try {
        const results = await Promise.all(
          campaigns.map(campaign =>
            context.dispatch(ACTIONS.DO_LOAD_AEM_CAMPAIGNS, campaign)
          )
        );

        const enrichedCampaigns = results.filter(Boolean);
        if (enrichedCampaigns.length === 0) {
          Vue.$log.error(
            "Todas las campañas AEM devolvieron error de respuesta"
          );

          context.commit(MUTATIONS.ON_PASO_2, payload.respuesta);
          context.commit(MUTATIONS.ON_IS_LOADING, false);
          router.push("paso2");
          return;
        }

        payload.respuesta.campanias = enrichedCampaigns;
        context.commit(MUTATIONS.ON_PASO_2, payload.respuesta);
        context.commit(MUTATIONS.ON_IS_LOADING, false);
        router.push("paso2");
      } catch (error) {
        Vue.$log.error("Error cargando campañas AEM:", error);
        context.dispatch(ACTIONS.DO_MENSAJE, "mensaje-4");
        context.commit(MUTATIONS.ON_IS_LOADING, false);
        return;
      }
    }
  },
  [ACTIONS.DO_PASO1_ERROR]: (context, payload) => {
    Vue.$log.error(payload);
    context.dispatch(ACTIONS.DO_MENSAJE, "mensaje-1");
    context.commit(MUTATIONS.ON_IS_LOADING, false);
    context.commit(MUTATIONS.ON_TIPO_ERROR, payload);
  },

  [ACTIONS.DO_PASO2]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.CONSULTAR_PREMIOS,
      doSuccess: ACTIONS.DO_PASO2_SUCCESS,
      doError: ACTIONS.DO_PASO4_ERROR,
      payload
    });
  },

  [ACTIONS.DO_PASO2_SUCCESS]: (context, payload) => {
    context.commit(MUTATIONS.ON_PASO_3, payload);
    context.commit(MUTATIONS.ON_IS_LOADING, false);
    router.push("paso3");
  },

  [ACTIONS.DO_PASO2_ERROR]: (context, payload) => {
    Vue.$log.error(payload);
    context.dispatch(ACTIONS.DO_MENSAJE, "mensaje-1");
    context.commit(MUTATIONS.ON_IS_LOADING, false);
  },

  [ACTIONS.DO_PASO3]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.CONSULTAR_HASH,
      doSuccess: ACTIONS.DO_PASO3_SUCCESS,
      doError: ACTIONS.DO_PASO3_ERROR,
      payload
    });
  },

  [ACTIONS.DO_PASO3_SUCCESS]: (context, payload) => {
    context.commit(MUTATIONS.ON_URL, payload);
    const tipoCampania = context.getters.getCampaniaSeleccionada();
    let urlPath = "";
    if (
      _BBVA_CAMP === "ofertaReferenteEmpleados" &&
      tipoCampania === tiposAReferir.REFERIR_TC
    ) {
      urlPath = process.env.VUE_APP_SRC_REFERIDO_EMPLEADO;
    } else if (tipoCampania === tiposAReferir.REFERIR_NOMINA) {
      urlPath = process.env.VUE_APP_SRC_REFERIDO_NOMINA;
    } else if (tipoCampania === tiposAReferir.REFERIR_JUBILADO) {
      urlPath = process.env.VUE_APP_SRC_REFERIDO_JUBILADO;
    } else if (tipoCampania === tiposAReferir.REFERIR_TC) {
      urlPath = process.env.VUE_APP_SRC_REFERIDO;
    }
    let url = new URL(urlPath);
    url.searchParams.set("hashref", context.getters.getPaso4().hash);
    context.dispatch("DO_PASO4", { url: url.href });
    if (router.history.current.name == "paso3") {
      router.push("paso4");
    }
  },

  [ACTIONS.DO_PASO3_ERROR]: (context, payload) => {
    Vue.$log.error(payload);
    context.dispatch(ACTIONS.DO_MENSAJE, "mensaje-1");
    context.commit(MUTATIONS.ON_IS_LOADING, false);
  },

  [ACTIONS.DO_PASO4]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.ACORTAR_URL,
      doSuccess: ACTIONS.DO_PASO4_SUCCESS,
      doError: ACTIONS.DO_PASO4_ERROR,
      payload
    });
  },
  [ACTIONS.DO_PASO4_SUCCESS]: (context, payload) => {
    context.commit(MUTATIONS.ON_PASO_4, payload);
    context.commit(MUTATIONS.ON_IS_LOADING, false);
  },
  [ACTIONS.DO_PASO4_ERROR]: (context, payload) => {
    Vue.$log.error(payload);
    context.dispatch(ACTIONS.DO_MENSAJE, "mensaje-1");
    context.commit(MUTATIONS.ON_IS_LOADING, false);
  },
  [ACTIONS.DO_GRACIAS]: (context, payload) => {
    context.commit(MUTATIONS.ON_GRACIAS, payload);
  },
  /**
   * Mostrar un mensaje de error
   * @param {Object} context
   * @param {Object} payload Código del mensaje
   */
  [ACTIONS.DO_MENSAJE]: (context, payload) => {
    router.push({
      name: "mensaje",
      params: {
        mje: payload
      }
    });
  },

  /**
   * Tokenizar usuario para la analitica
   * @param {Object} context
   * @param {Object} payload
   */
  [ACTIONS.DO_TOKENIZE_USER]: (context, payload) => {
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.TOKENIZAR_USER,
      doSuccess: ACTIONS.DO_TOKENIZE_USER_SUCCESS,
      doError: ACTIONS.DO_TOKENIZE_USER_ERROR,
      payload
    });
  },
  [ACTIONS.DO_TOKENIZE_USER_SUCCESS]: (context, payload) => {
    context.commit(
      MUTATIONS.ON_USER_ID_TOKENIZADO,
      get(payload, "respuesta.customerID", "")
    );
  },
  [ACTIONS.DO_TOKENIZE_USER_ERROR]: payload => {
    Vue.$log.error(payload);
  },

  /**
   * Cargar contenido dinámico de una campaña desde AEM
   * @param {Object} context
   * @param {Object} campaign Campaña individual a procesar
   * @returns {Promise<Object>} Promesa con la campaña procesada
   */
  [ACTIONS.DO_LOAD_AEM_CAMPAIGNS]: (context, campaign) => {
    if (!campaign) {
      return Promise.resolve(null);
    }

    const file = `config-${campaign.idCamp}`;
    const endpoint = `${END_POINTS.AEM_CAMPAIGN}/${file}`;

    return om
      .request({ endPoint: endpoint, method: "GET" })
      .then(response => {
        // Si me comunico pero trae un error navego a la prox campaña
        if (response.data.errorCode || response.data.status !== "OK") {
          context.dispatch(ACTIONS.DO_LOAD_AEM_CAMPAIGNS_ERROR, {
            campaign
          });

          return null;
        } else {
          return context.dispatch(ACTIONS.DO_LOAD_AEM_CAMPAIGNS_SUCCESS, {
            campaign,
            response: response.data
          });
        }
      })
      .catch(error => {
        // Si tengo un problema de comunicacion voy directo al error
        context.dispatch(ACTIONS.DO_LOAD_AEM_CAMPAIGNS_ERROR, {
          campaign
        });

        throw error;
      });
  },

  [ACTIONS.DO_LOAD_AEM_CAMPAIGNS_SUCCESS]: (
    context,
    { campaign, response }
  ) => {
    const campaignCopy = { ...campaign };

    const rta = response?.respuesta ? JSON.parse(response.respuesta) : null;

    if (rta && Object.keys(rta).length !== 0) {
      campaignCopy.dynamicContent = {
        pathImage: rta.aemref + rta.pathimage,
        title: rta.title,
        description: rta.description,
        list: rta.bullets ? JSON.parse(rta.bullets) : [],
        textButton: rta.textbutton,
        newReferredInformationText: cleanHtmlTags(rta.compartirlink) || "",
        // TODA ESTA INFO NO SE USA PARA LAS CARDS
        subTitle: rta.subtitle,
        badge: rta.badge,
        plantillas: {
          condicionesGenerales: rta.condicionesgenerales,
          premiosDisponibles: rta.premiosdisponibles,
          canjeoPremios: rta.canjeopremios
        },
        textLink: rta.textlink
      };
    }

    return campaignCopy;
  },

  [ACTIONS.DO_LOAD_AEM_CAMPAIGNS_ERROR]: (context, { campaign }) => {
    Vue.$log.error(
      `Error cargando configuración AEM para campaña ${campaign.idCamp}`
    );
  }
};
